/**
 * Settings › Teams: who works together, who leads them, what a rep sees,
 * and the rotations that give reps new people.
 *
 * A team lives on the gateway beside its members. Leading a team widens a
 * sales rep's view from their own records to everyone's in the team; the
 * change reaches their lists within about five minutes, as a change of role
 * does. Other roles see every record whatever their teams.
 *
 * For admins and anyone with manage_teams, and only while the CRM area is
 * on: teams and rotations are CRM.
 */
import { useState } from 'react';
import {
  ActionIcon, Alert, Badge, Button, Card, Group, Select, Stack, Switch, Table, Text, TextInput, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconPlus, IconTrash, IconUsers } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useCrmAccess, useSaveCrmAccess } from '../../api/assignment';
import { tenantUsers } from '../../api/gateway';
import { memberName, useTeamMutations, useTeams, type Team } from '../../api/teams';
import { useAuth } from '../../auth/AuthProvider';
import { roleLabel, useVocab } from '../../vocab';
import { RotationsPanel } from '../assignment/Rotations';

const NO_TEAMS = new Set(['buyer', 'supplier', 'unapproved', 'super_admin']);

function useMembersToChoose(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['tenant-users', tenantId],
    queryFn: () => tenantUsers(tenantId!),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}

function TeamCard({ team, tenantId }: { team: Team; tenantId: string }) {
  const v = useVocab();
  const m = useTeamMutations(tenantId);
  const users = useMembersToChoose(tenantId).data?.users ?? [];
  const [name, setName] = useState(team.name);
  const [adding, setAdding] = useState<string | null>(null);
  const members = team.members.map((x) => ({ user_id: x.user_id, is_lead: x.is_lead }));
  const choose = users
    .filter((u) => !NO_TEAMS.has(u.role) && u.status === 'active' && !members.some((x) => x.user_id === u.id))
    .map((u) => ({ value: u.id, label: memberName(u) }));

  async function save(next: { user_id: string; is_lead: boolean }[], what: string) {
    try {
      await m.members.mutateAsync({ id: team.id, members: next });
      notifications.show({ color: 'teal', title: team.name, message: what });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not saved', message: (e as Error).message });
    }
  }

  return (
    <Card withBorder p="md" data-testid={`team-${team.id}`}>
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <TextInput
            aria-label={`${v.One('team')} name`}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            w={260}
          />
          {name.trim() && name.trim() !== team.name && (
            <ActionIcon
              aria-label="Save name"
              variant="light"
              onClick={() => m.rename.mutate({ id: team.id, name: name.trim() }, {
                onError: (e) => notifications.show({ color: 'red', title: 'Not renamed', message: e.message }),
              })}
            >
              <IconCheck size={16} />
            </ActionIcon>
          )}
        </Group>
        <Button
          size="xs"
          variant="subtle"
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={() => {
            if (window.confirm(`Delete ${team.name}? Its members stay; anyone who led only this ${v.one('team')} goes back to seeing their own records.`)) {
              m.remove.mutate(team.id);
            }
          }}
        >
          {`Delete ${v.one('team')}`}
        </Button>
      </Group>
      <Table verticalSpacing={6}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Member</Table.Th>
            <Table.Th>Role</Table.Th>
            <Table.Th>Leader</Table.Th>
            <Table.Th w={40} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {team.members.length === 0 && (
            <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed">Nobody yet.</Text></Table.Td></Table.Tr>
          )}
          {team.members.map((x) => (
            <Table.Tr key={x.user_id} data-testid={`member-${x.user_id}`}>
              <Table.Td>
                <Text size="sm" fw={500}>{memberName(x)}</Text>
                <Text size="xs" c="dimmed">{x.email}</Text>
              </Table.Td>
              <Table.Td><Badge variant="light" color="gray" tt="none">{roleLabel(v, x.role)}</Badge></Table.Td>
              <Table.Td>
                <Switch
                  aria-label={`${memberName(x)} is a leader of ${team.name}`}
                  checked={x.is_lead}
                  onChange={(e) => {
                    const lead = e.currentTarget.checked;
                    save(
                      members.map((y) => (y.user_id === x.user_id ? { ...y, is_lead: lead } : y)),
                      lead ? `${memberName(x)} is now a leader.` : `${memberName(x)} is no longer a leader.`,
                    );
                  }}
                />
              </Table.Td>
              <Table.Td>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Remove ${memberName(x)}`}
                  onClick={() => save(members.filter((y) => y.user_id !== x.user_id), `${memberName(x)} left.`)}
                >
                  <IconTrash size={15} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group gap="xs" mt="sm">
        <Select
          aria-label={`Add to ${team.name}`}
          placeholder="Add a member"
          searchable
          data={choose}
          value={adding}
          onChange={setAdding}
          w={260}
          nothingFoundMessage="Everyone is in already"
        />
        <Button
          variant="default"
          leftSection={<IconPlus size={14} />}
          disabled={!adding}
          onClick={() => {
            if (!adding) return;
            const who = users.find((u) => u.id === adding);
            save([...members, { user_id: adding, is_lead: false }], `${who ? memberName(who) : 'They'} joined.`);
            setAdding(null);
          }}
        >
          Add
        </Button>
      </Group>
    </Card>
  );
}

/** Whether reps see records nobody owns. Admins only. */
function UnownedCard({ isAdmin }: { isAdmin: boolean }) {
  const v = useVocab();
  const access = useCrmAccess();
  const save = useSaveCrmAccess();
  const on = access.data?.sales_see_unowned ?? true;
  return (
    <Card withBorder p="md" data-testid="unowned-card">
      <Switch
        label={`Sales reps see ${v.many('person')}, ${v.many('company')} and ${v.many('deal')} nobody owns`}
        description={`A rep always sees their own, and a ${v.one('team')} leader their ${v.one('team')}'s. With this off, unowned records are hidden from them until someone takes them or assigns them.`}
        checked={on}
        disabled={!isAdmin || access.isPending}
        onChange={(e) => save.mutate({ sales_see_unowned: e.currentTarget.checked })}
      />
    </Card>
  );
}

export function TeamsTab() {
  const v = useVocab();
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const teams = useTeams(tenantId);
  const m = useTeamMutations(tenantId);
  const [newName, setNewName] = useState('');

  return (
    <Stack gap="lg" maw={1000}>
      <div>
        <Group gap="xs"><IconUsers size={18} /><Title order={4}>{v.Many('team')}</Title></Group>
        <Text size="sm" c="dimmed" maw={680}>
          {`A sales rep sees the records they own. A leader of a ${v.one('team')} sees everything its members own too. Managers and admins see everything. Changes reach everyone within about five minutes.`}
        </Text>
      </div>

      <UnownedCard isAdmin={isAdmin} />

      {teams.error && <Alert color="red" variant="light">{teams.error.message}</Alert>}
      {(teams.data?.teams ?? []).map((t) => (
        <TeamCard key={`${t.id}:${t.name}`} team={t} tenantId={tenantId!} />
      ))}
      {teams.data && teams.data.teams.length === 0 && (
        <Text size="sm" c="dimmed">{`No ${v.many('team')} yet.`}</Text>
      )}
      <Group gap="xs">
        <TextInput
          aria-label={`New ${v.one('team')} name`}
          placeholder={`New ${v.one('team')} name`}
          value={newName}
          onChange={(e) => setNewName(e.currentTarget.value)}
          w={260}
        />
        <Button
          leftSection={<IconPlus size={14} />}
          disabled={!newName.trim()}
          loading={m.create.isPending}
          onClick={() => m.create.mutate(newName.trim(), {
            onSuccess: () => setNewName(''),
            onError: (e) => notifications.show({ color: 'red', title: 'Not added', message: e.message }),
          })}
        >
          {`Add ${v.one('team')}`}
        </Button>
      </Group>

      <RotationsPanel />
    </Stack>
  );
}
